import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  portalLoginApi,
  type PortalAuthSource,
  type PortalLoginApi,
  type PortalLoginOptions,
} from './portalLoginApi';

export interface PortalLoginViewModel {
  state: 'loading' | 'ready' | 'unavailable' | 'redirecting';
  mode: 'platform' | 'tenant';
  host: string;
  tenantName: string | null;
  brandingRef: string | null;
  primaryAction: { source: PortalAuthSource; label: string } | null;
  fallbackAction: { source: 'platform_public'; label: string } | null;
  registrationAction: { label: string } | null;
  message: string | null;
  onPrimary: () => void;
  onFallback: () => void;
  onRegister: () => void;
  onRetry: () => void;
}

export interface PortalLoginControllerOptions {
  api?: PortalLoginApi;
  currentPath?: string;
  navigate?: (url: string) => void;
}

const safeReturnPath = (value: string | undefined): string | undefined => {
  if (!value || !value.startsWith('/') || value.startsWith('//')) return undefined;
  try {
    const parsed = new URL(value, 'https://portal.invalid');
    return parsed.origin === 'https://portal.invalid' ? `${parsed.pathname}${parsed.search}${parsed.hash}` : undefined;
  } catch {
    return undefined;
  }
};

const tenantSelectorsFromPath = (value: string | undefined): string[] => {
  if (!value) return [];
  try {
    return new URL(value, 'https://portal.invalid').searchParams.getAll('tenant');
  } catch {
    return [];
  }
};

export function buildPortalLoginViewModel(input: {
  options: PortalLoginOptions | null;
  loading: boolean;
  redirecting: boolean;
  error: string | null;
  start: (source: PortalAuthSource, register?: boolean) => void;
  retry: () => void;
}): PortalLoginViewModel {
  const { options, loading, redirecting, error, start, retry } = input;
  const ready = Boolean(options?.available);
  return {
    state: redirecting ? 'redirecting' : loading ? 'loading' : ready ? 'ready' : 'unavailable',
    mode: options?.mode ?? 'platform',
    host: options?.host ?? '',
    tenantName: options?.tenant?.displayName ?? null,
    brandingRef: options?.tenant?.brandingRef ?? null,
    primaryAction: ready && options ? options.primary : null,
    fallbackAction: options?.fallback?.source === 'platform_public' ? options.fallback : null,
    registrationAction: options?.mode === 'platform' && ready ? { label: 'Create account' } : null,
    message: error,
    onPrimary: () => options && start(options.primary.source),
    onFallback: () => options?.fallback && start(options.fallback.source),
    onRegister: () => start('platform_public', true),
    onRetry: retry,
  };
}

export function usePortalLoginController(controllerOptions: PortalLoginControllerOptions = {}): PortalLoginViewModel {
  const api = controllerOptions.api ?? portalLoginApi;
  const currentPath = safeReturnPath(controllerOptions.currentPath ?? `${window.location.pathname}${window.location.search}${window.location.hash}`);
  const tenantSelectors = useMemo(() => tenantSelectorsFromPath(currentPath), [currentPath]);
  const navigate = controllerOptions.navigate ?? ((url: string) => window.location.assign(url));
  const [options, setOptions] = useState<PortalLoginOptions | null>(null);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    const abort = new AbortController();
    setLoading(true);
    setError(null);
    api.resolveOptions(tenantSelectors, abort.signal)
      .then(setOptions)
      .catch((reason: unknown) => {
        if (abort.signal.aborted) return;
        setOptions(null);
        setError(reason instanceof Error ? reason.message : 'Tenant login is currently unavailable.');
      })
      .finally(() => {
        if (!abort.signal.aborted) setLoading(false);
      });
    return () => abort.abort();
  }, [api, revision, tenantSelectors]);

  const start = useCallback((source: PortalAuthSource, register = false) => {
    setRedirecting(true);
    setError(null);
    void api.start({
      source,
      returnPath: currentPath,
      register: register || undefined,
      tenantSlug: options?.tenant?.slug,
    })
      .then(({ redirectUrl }) => navigate(redirectUrl))
      .catch((reason: unknown) => {
        setRedirecting(false);
        setError(reason instanceof Error ? reason.message : 'Login could not be started.');
      });
  }, [api, currentPath, navigate, options?.tenant?.slug]);

  const retry = useCallback(() => setRevision((value) => value + 1), []);
  return useMemo(() => buildPortalLoginViewModel({ options, loading, redirecting, error, start, retry }), [error, loading, options, redirecting, retry, start]);
}

export { safeReturnPath, tenantSelectorsFromPath };
